import { Component, OnInit } from '@angular/core'
import { Location } from '@angular/common'
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  Validators,
} from '@angular/forms'
import { Router, ActivatedRoute } from '@angular/router'
import { NzDividerModule } from 'ng-zorro-antd/divider'
import { NzFormLayoutType } from 'ng-zorro-antd/form'
import { NzNotificationService } from 'ng-zorro-antd/notification'
import { AtomModule } from '../../atom/atom.module'
import { OnboardingProgressState } from '../../gql/generated'
import { AppleSigninComponent } from '../../molecule/apple-signin/apple-signin.component'
import { DiscordSigninComponent } from '../../molecule/discord-signin/discord-signin.component'
import { FacebookSigninComponent } from '../../molecule/facebook-signin/facebook-signin.component'
import { GoogleSigninComponent } from '../../molecule/google-signin/google-signin.component'
import { MicrosoftSigninComponent } from '../../molecule/microsoft-signin/microsoft-signin.component'
import { AppService } from '../../service/app.service'
import { AuthenticationService } from '../../service/authentication.service'
import { CustomerService } from '../../service/customer.service'
import { OnboardingService } from '../../service/onboarding.service'
import { NzInputModule } from 'ng-zorro-antd/input'
import { forkJoin, map, mergeMap, of } from 'rxjs'
import { on } from 'events'
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.less',
  standalone: true,
  imports: [
    AtomModule,
    DiscordSigninComponent,
    MicrosoftSigninComponent,
    GoogleSigninComponent,
    FacebookSigninComponent,
    AppleSigninComponent,
    NzDividerModule,
    NzInputModule,
  ],
})
export class LoginComponent implements OnInit {
  validateForm: FormGroup<{
    formLayout: FormControl<NzFormLayoutType>
    email: FormControl<string>
    password: FormControl<string>
  }> = this.formBuilder.group({
    formLayout: 'horizontal' as NzFormLayoutType,
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]],
  })

  passwordVisible = false

  pluginName = ''

  constructor(
    private formBuilder: NonNullableFormBuilder,
    private authenticationService: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute,
    public appService: AppService,
    private location: Location,
    private customerService: CustomerService,
    private nzNotificationService: NzNotificationService,
    private onboardingService: OnboardingService
  ) {}

  ngOnInit() {
    this.authenticationService.authStore$.subscribe((store) => {
      this.pluginName = store.pluginName
    })
    this.route.queryParams.subscribe((params) => {
      const provider = params['provider']
      const code = params['code']
      const token = params['token']
      if (provider === 'discord' && code) {
        this.onDiscordToken(code)
        // Remove query parameters from the URL
        this.location.replaceState(this.location.path().split('?')[0])
      }
      if (provider === 'apple' && token) {
        this.onAppleToken(token)
        // Remove query parameters from the URL
        this.location.replaceState(this.location.path().split('?')[0])
      }
    })
  }

  submitForm() {
    this.validateForm.markAllAsTouched()
    this.validateForm.updateValueAndValidity()
    this.validateForm.validate()

    if (this.validateForm.valid) {
      const email = this.validateForm.controls.email.value
      const password = this.validateForm.controls.password.value
      this.authenticationService
        .login({
          native: {
            username: email,
            password,
          },
        })
        .subscribe((response) => {
          const error = response.data?.authenticate as {
            errorCode?: string
            message?: string
          }
          if (!error?.errorCode) {
            this.redirectAfterLogin()
          }
        })
    }
  }

  loginClick() {
    console.log('login click')
  }

  onDiscordToken(code: string) {
    this.authenticationService
      .login({
        discord_customer: {
          // the code means OAuth2 authentication code, we need to get the access_token from the server
          token: code,
          page: 'login'
        },
      })
      .subscribe(() => {
        this.redirectAfterLogin()
      })
  }

  onGoogleToken(token: string) {
    this.authenticationService
      .login({
        google_customer: {
          token: token,
        },
      })
      .subscribe(() => {
        this.redirectAfterLogin()
      })
  }

  onFacebookToken(token: string) {
    console.log('facebook token', token)
  }

  onMicrosoftToken(token: string) {
    this.authenticationService
      .login({
        microsoft_customer: {
          token: token,
        },
      })
      .subscribe(() => {
        this.redirectAfterLogin()
      })
  }

  onAppleToken(token: string) {
    this.authenticationService
    .login({
      apple_customer: {
        token: token,
      },
    })
    .subscribe(() => {
      this.redirectAfterLogin()
    })
  }

  redirectAfterLogin() {
    this.customerService
      .getActiveCustomer()
      .pipe(
        mergeMap((activeCustomerResp) => {
          const activeCustomer = activeCustomerResp?.data?.activeCustomer
          if (activeCustomer) {
            return this.onboardingService.getOnboarding(activeCustomer.id).pipe(
              map((onboardingData) => ({ activeCustomerResp, onboardingData }))
            );
          }else{
            return of({ activeCustomerResp, onboardingData: null });
          }
        }),
        mergeMap(({ activeCustomerResp, onboardingData }) => {
          const onboarding = onboardingData?.data?.getOnboarding
          if(onboarding){
            const resolveUrl = onboarding.resolveUrl ?? '/home'
            this.router.navigate([resolveUrl])
            return of(null)
          }else if(activeCustomerResp){
            return this.onboardingService.createOnboarding({
              emailAddress: activeCustomerResp.data.activeCustomer?.emailAddress as string,
              resolveUrl: this.authenticationService.authStore$.state.resolveUrl,
              state: OnboardingProgressState.CUSTOMER_REGISTER_3PARTY
            }).pipe(map(() => {
              this.router.navigate(['/register-complete'])
            }))
          }else{
            return of(null)
          }
        })
      ).subscribe()
  }

  get f(): { [key: string]: AbstractControl } {
    return this.validateForm.controls
  }
}
